import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, RotateCcw, Edit3, Clock, AlertTriangle, Pencil, Trash2, Check, X } from 'lucide-react';
import client from '../../api/client';
import { appInsights } from '../../lib/appInsights';
import { useAuth, userIsAdmin, userIsReviewer, userIsEngineer, userIsManager } from '../../context/AuthContext';
import { NAVY, AMBER, BORDER_COLOR, bubbleStyle, navyBtn, fieldStyle } from '../../styles/tokens';
import NominaSendBackModal from '../components/NominaSendBackModal';
import SearchableSelect from '../../shared/components/SearchableSelect';
import { ABSENCE_REASONS, STATUS_STYLE } from '@shared/constants';

// Colors from the shared STATUS_STYLE (single source); labels stay local (unchanged).
const STATUS_CONFIG = {
  draft:            { label: 'Borrador',            ...STATUS_STYLE.draft },
  submitted:        { label: 'Pendiente Ingeniero', ...STATUS_STYLE.submitted },
  pending_engineer: { label: 'Pendiente Ingeniero', ...STATUS_STYLE.pending_engineer },
  pending_manager:  { label: 'Pendiente Gerente',   ...STATUS_STYLE.pending_manager },
  pending_clerk:    { label: 'Rev. Final',          ...STATUS_STYLE.pending_clerk },
  approved:         { label: 'Aprobado',            ...STATUS_STYLE.approved },
  returned:         { label: 'Devuelto',            ...STATUS_STYLE.returned },
};

function fmtDate(d) {
  if (!d) return '';
  const [y, m, day] = String(d).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDateTime(d) {
  return new Date(d).toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function TarjetaDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [editReason, setEditReason] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [showReturnModal, setShowReturnModal] = useState(false);

  // AMGtime validation
  const [amgValidation, setAmgValidation] = useState(null);
  const [amgLoading, setAmgLoading] = useState(false);
  const [amgError, setAmgError] = useState(null);

  // Edit mode (engineer, reviewer, admin)
  const [editMode, setEditMode] = useState(false);
  const [editedEntries, setEditedEntries] = useState({}); // { entryId: { regular_hours, overtime_hours, cost_code_id } }

  // Absence inline edit during review
  const [editingAbsenceId, setEditingAbsenceId] = useState(null);
  const [editingAbsData, setEditingAbsData] = useState({ reason: 'class', notes: '' });
  const [costCodes, setCostCodes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [addedEmployees, setAddedEmployees] = useState([]); // { empId, empName, entries: [{ costCodeId, regular, overtime }] }
  const [empAlerts, setEmpAlerts] = useState({}); // { employeeId: alertData }

  useEffect(() => {
    client.get(`/api/nomina/sheets/${id}`)
      .then(r => {
        setSheet(r.data);
        setLoading(false);

        // Fire AMGtime + a single batched hours-check (one POST for all employees)
        // in parallel — V27.2 replaced the per-employee N parallel GETs that
        // were hitting the per-IP rate limiter on large gangas.
        const parallel = [];

        if (r.data.status !== 'draft') {
          setAmgLoading(true);
          parallel.push(
            client.get(`/api/nomina/sheets/${id}/amgtime-validation`)
              .then(res => setAmgValidation(res.data))
              .catch(err => setAmgError(err.response?.data?.error || 'Error al consultar AMGtime'))
              .finally(() => setAmgLoading(false))
          );
        }

        const sheetDate = String(r.data.sheet_date).slice(0, 10);
        const uniqueEmpIds = [...new Set((r.data.entries || []).map(e => e.employee_id))];
        if (uniqueEmpIds.length > 0) {
          parallel.push(
            client.post('/api/nomina/hours-check/batch', {
              employeeIds: uniqueEmpIds,
              date: sheetDate,
              excludeSheetId: id,
            })
              .then(res => setEmpAlerts(prev => ({ ...prev, ...(res.data || {}) })))
              .catch(() => {})
          );
        }

        return Promise.all(parallel);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
        navigate('/horas');
      });
  }, [id, navigate]);

  // Fetch cost codes and employees when entering edit mode
  useEffect(() => {
    if (editMode && sheet) {
      if (costCodes.length === 0) {
        client.get('/api/nomina/cost-codes', { params: { project_id: sheet.project_id } })
          .then(r => setCostCodes(r.data))
          .catch(console.error);
      }
      if (employees.length === 0) {
        client.get('/api/nomina/employees', { params: { project_id: sheet.project_id } })
          .then(r => setEmployees(r.data))
          .catch(console.error);
      }
    }
  }, [editMode, sheet]);

  // V26.3 Bug 3: when admin/reviewer adds a NEW employee during edit mode,
  // fetch cross-project hours-check so the same cap-bubbles (D: X/8h, S: X/40h)
  // show on the new employee card. Mirrors the TarjetaPage editing flow.
  useEffect(() => {
    if (!sheet || addedEmployees.length === 0) return;
    const sheetDate = String(sheet.sheet_date).slice(0, 10);
    const pendingIds = addedEmployees
      .map(ae => ae.empId)
      .filter(empId => empId && !empAlerts[empId]);
    if (pendingIds.length === 0) return;
    client.post('/api/nomina/hours-check/batch', {
      employeeIds: pendingIds,
      date: sheetDate,
      excludeSheetId: id,
    })
      .then(res => setEmpAlerts(prev => ({ ...prev, ...(res.data || {}) })))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addedEmployees.map(a => a.empId).join(','), sheet?.id]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Cargando...</div>;
  if (!sheet) return null;

  const cfg = STATUS_CONFIG[sheet.status] || STATUS_CONFIG.draft;
  const isAdm = userIsAdmin(user);
  const isRev = userIsReviewer(user);
  // Level-aware: engineer approves L1, manager approves L2, reviewer approves final
  const reviewableStatuses = ['submitted', 'pending_engineer', 'pending_manager', 'pending_clerk'];
  // V26.1: reviewer-for-project takeover at engineer/manager stages;
  // manager-on-ganga takeover at engineer stage.
  const isProjectReviewerTakeover = sheet.is_current_user_nomina_reviewer && isRev;
  const isManagerTakeover = sheet.is_current_user_ganga_manager && user.roles?.includes('manager');
  // V30: any-of approval — array-based check against the project's ganga members.
  // Singular *_id fields still supported as fallback for stale clients.
  const engineerIds = sheet.project_engineer_ids || (sheet.project_engineer_id ? [sheet.project_engineer_id] : []);
  const managerIds  = sheet.project_manager_ids  || (sheet.project_manager_id  ? [sheet.project_manager_id]  : []);
  const isProjectEngineer = engineerIds.includes(user.id);
  const isProjectManager  = managerIds.includes(user.id);
  const canReview = reviewableStatuses.includes(sheet.status) && (
    isAdm ||
    (sheet.status === 'submitted'        && (isProjectEngineer || isProjectReviewerTakeover || isManagerTakeover)) ||
    (sheet.status === 'pending_engineer' && (isProjectEngineer || isProjectReviewerTakeover || isManagerTakeover)) ||
    (sheet.status === 'pending_manager'  && (isProjectManager  || isProjectReviewerTakeover)) ||
    (sheet.status === 'pending_clerk'    && isRev)
  );
  const hasEdits = Object.keys(editedEntries).length > 0 || addedEmployees.length > 0;
  const canSeeAmg = isAdm || userIsEngineer(user) || userIsManager(user) || isRev;

  // Find AMGtime data for a specific employee (by name match)
  function getAmgForEmployee(empName) {
    if (!amgValidation?.validated || !empName) return null;
    const matched = amgValidation.matched?.find(m => m.name === empName);
    if (matched) return { status: 'match' };
    const disc = amgValidation.discrepancies?.find(d => d.name === empName);
    if (disc) return { status: disc.type, ...disc };
    return null;
  }

  // Group entries by employee
  const byEmployee = {};
  (sheet.entries || []).forEach(e => {
    if (!byEmployee[e.employee_id]) {
      byEmployee[e.employee_id] = { name: e.employee_name, workLocation: e.work_location || 'in-person', entries: [] };
    }
    byEmployee[e.employee_id].entries.push(e);
  });
  const empGroups = Object.entries(byEmployee);

  function updateEditedEntry(entryId, field, value) {
    setEditedEntries(prev => ({
      ...prev,
      [entryId]: {
        ...prev[entryId],
        [field]: field === 'cost_code_id' ? parseInt(value) : field === 'notes' ? value : (parseFloat(value) || 0),
      },
    }));
  }

  function getEntryValue(entry, field) {
    if (editedEntries[entry.id] && editedEntries[entry.id][field] !== undefined) {
      return editedEntries[entry.id][field];
    }
    if (field === 'cost_code_id') return entry[field];
    if (field === 'notes') return entry[field] || '';
    return parseFloat(entry[field]) || 0;
  }

  // ── Absence inline edit/delete during review ────────────────────────────
  function startEditAbsence(a) {
    setEditingAbsenceId(a.id);
    setEditingAbsData({ reason: a.reason, notes: a.notes || '' });
  }

  async function saveAbsenceEdit() {
    if (!editingAbsenceId) return;
    try {
      const res = await client.patch(`/api/nomina/absences/${editingAbsenceId}`, {
        reason: editingAbsData.reason,
        notes: editingAbsData.notes || null,
      });
      setSheet(prev => ({
        ...prev,
        absences: (prev.absences || []).map(a =>
          a.id === editingAbsenceId ? { ...a, ...res.data } : a
        ),
      }));
      setEditingAbsenceId(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al actualizar ausencia.');
    }
  }

  async function removeAbsence(absenceId) {
    try {
      await client.delete(`/api/nomina/absences/${absenceId}`);
      setSheet(prev => ({
        ...prev,
        absences: (prev.absences || []).filter(a => a.id !== absenceId),
      }));
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar ausencia.');
    }
  }

  function refreshAmgValidation() {
    setAmgLoading(true);
    client.get(`/api/nomina/sheets/${id}/amgtime-validation`)
      .then(r => setAmgValidation(r.data))
      .catch(err => setAmgError(err.response?.data?.error || 'Error'))
      .finally(() => setAmgLoading(false));
  }

  async function handleApprove() {
    setActionLoading('approve');
    try {
      await client.post(`/api/nomina/sheets/${id}/approve`, { notes: notes || null });
      const res = await client.get(`/api/nomina/sheets/${id}`);
      setSheet(res.data);
      refreshAmgValidation();
      setEditMode(false);
      setEditedEntries({});
    } catch (err) {
      appInsights?.trackException({
        exception: err,
        properties: {
          context: 'nomina_sheet_approve',
          sheet_id: id,
          sheet_status: sheet?.status,
        },
      });
      alert(err.response?.data?.error || 'Error.');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleApproveWithEdits() {
    const hasAddedEmps = addedEmployees.some(a => a.entries.some(e => e.costCodeId && (e.regular > 0 || e.overtime > 0)));
    if (!hasEdits && !hasAddedEmps) return handleApprove();
    setActionLoading('approve-edits');
    try {
      const entries = Object.entries(editedEntries).map(([entryId, changes]) => ({
        entry_id: parseInt(entryId),
        ...changes,
      }));
      // Include new employees (V26.3 Bug 3: carry employee-level notes too)
      const newEntries = [];
      for (const ae of addedEmployees) {
        for (const e of ae.entries) {
          if (e.costCodeId && (e.regular > 0 || e.overtime > 0)) {
            newEntries.push({
              employee_id: ae.empId,
              cost_code_id: e.costCodeId,
              regular_hours: e.regular || 0,
              overtime_hours: e.overtime || 0,
              notes: ae.empNotes || null,
            });
          }
        }
      }
      await client.post(`/api/nomina/sheets/${id}/approve-with-edits`, {
        entries,
        newEntries,
        reason: editReason || null,
        notes: notes || null,
      });
      const res = await client.get(`/api/nomina/sheets/${id}`);
      setSheet(res.data);
      refreshAmgValidation();
      setEditMode(false);
      setEditedEntries({});
    } catch (err) {
      appInsights?.trackException({
        exception: err,
        properties: {
          context: 'nomina_sheet_approve_with_edits',
          sheet_id: id,
          sheet_status: sheet?.status,
        },
      });
      alert(err.response?.data?.error || 'Error.');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleReturn({ reason, notes: modalNotes } = {}) {
    const finalReason = reason || returnReason;
    if (!finalReason?.trim()) {
      alert('La razón de devolución es requerida.');
      return;
    }
    setShowReturnModal(false);
    setActionLoading('return');
    try {
      await client.post(`/api/nomina/sheets/${id}/return`, { return_reason: finalReason, notes: modalNotes || notes || null });
      const res = await client.get(`/api/nomina/sheets/${id}`);
      setSheet(res.data);
    } catch (err) {
      appInsights?.trackException({
        exception: err,
        properties: {
          context: 'nomina_sheet_return',
          sheet_id: id,
          sheet_status: sheet?.status,
        },
      });
      alert(err.response?.data?.error || 'Error.');
    } finally {
      setActionLoading(null);
    }
  }

  // Grand totals
  const grandRegular = (sheet.entries || []).reduce((s, e) => s + getEntryValue(e, 'regular_hours'), 0);
  const grandOvertime = (sheet.entries || []).reduce((s, e) => s + getEntryValue(e, 'overtime_hours'), 0);

  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 800, margin: '0 auto' }}>
      {/* Back */}
      <button onClick={() => navigate(-1)} style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'none', border: 'none', cursor: 'pointer',
        color: '#64748b', fontSize: 13, marginBottom: 14, fontFamily: 'Inter, sans-serif',
      }}>
        <ArrowLeft size={16} /> Volver
      </button>

      {/* Header */}
      <div style={{ ...bubbleStyle, padding: '16px 20px', marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <h1 style={{ fontSize: 16, fontWeight: 700, color: NAVY, margin: '0 0 3px' }}>
              {sheet.project_code ? `${sheet.project_code} ` : ''}{sheet.project_name}
            </h1>
            <div style={{ fontSize: 13, color: '#64748b' }}>
              {fmtDate(sheet.sheet_date)} · Creado por: {sheet.supervisor_name}
            </div>
            {sheet.last_edited_by_user_id
              && sheet.last_edited_by_user_id !== sheet.supervisor_id
              && sheet.last_editor_name
              && sheet.takeover_target_name && (
              <div style={{
                display: 'inline-block', marginTop: 4, padding: '2px 8px',
                background: '#fef3c7', color: '#92400e',
                border: '1px solid #fde68a', borderRadius: 10,
                fontSize: 11, fontWeight: 600,
              }}>
                Editado por {sheet.last_editor_name} en nombre de {sheet.takeover_target_name}
              </div>
            )}
          </div>
          <span style={{
            padding: '3px 12px', borderRadius: 12,
            background: cfg.bg, color: cfg.color,
            fontSize: 11, fontWeight: 600,
          }}>
            {cfg.label}
          </span>
        </div>
      </div>

      {/* Return reason (non-editable) */}
      {sheet.return_reason && (
        <div style={{
          ...bubbleStyle, padding: '12px 16px', marginBottom: 14,
          borderLeft: '3px solid #ea580c', background: '#fff7ed',
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#ea580c', marginBottom: 2 }}>
            Razón de devolución:
          </div>
          <div style={{ fontSize: 13, color: '#9a3412' }}>{sheet.return_reason}</div>
        </div>
      )}

      {/* Reviewer notes */}
      {sheet.reviewer_notes && sheet.reviewer_notes !== sheet.return_reason && (
        <div style={{
          ...bubbleStyle, padding: '12px 16px', marginBottom: 14,
          borderLeft: `3px solid ${STATUS_CONFIG[sheet.status]?.color || '#94a3b8'}`,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 2 }}>
            Notas de {sheet.reviewer_name || 'Revisor'}:
          </div>
          <div style={{ fontSize: 13, color: NAVY }}>{sheet.reviewer_notes}</div>
        </div>
      )}

      {/* Approval level info */}
      {/* Holiday / Weekend global banner */}
      {(() => {
        const firstAlert = Object.values(empAlerts)[0];
        if (!firstAlert) return null;
        if (firstAlert.is_holiday) return (
          <div style={{ ...bubbleStyle, padding: '10px 16px', marginBottom: 14, borderLeft: '3px solid #7c3aed', background: '#f5f3ff', fontSize: 13, color: '#5b21b6', fontWeight: 500 }}>
            🏖 Este día es feriado: <strong>{firstAlert.holiday_name}</strong>
          </div>
        );
        if (firstAlert.is_weekend) return (
          <div style={{ ...bubbleStyle, padding: '10px 16px', marginBottom: 14, borderLeft: '3px solid #64748b', background: '#f8fafc', fontSize: 13, color: '#475569', fontWeight: 500 }}>
            📅 Este día es fin de semana
          </div>
        );
        return null;
      })()}

      {/* Approval chain */}
      {sheet.status !== 'draft' && sheet.status !== 'approved' && (
        <div style={{
          ...bubbleStyle, padding: '10px 16px', marginBottom: 14,
          display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
          background: '#f8fafc',
        }}>
          <span style={{ fontSize: 12, color: '#64748b', marginRight: 4 }}>Aprobación:</span>
          {(() => {
            const is3 = sheet.approval_type === '3_levels';
            // V30.1: for completed steps show the actual approver (per-level actor name
            // from audit_log; survives return + re-approve by a different engineer/manager).
            // For current / pending steps show the ROLE label since any approver could act.
            const ENG = 'Ingeniero(a)';
            const MGR = 'Gerente';
            const CLK = 'Rev. Final';
            const engDoneLabel  = sheet.engineer_actor_name || ENG;
            const mgrDoneLabel  = sheet.manager_actor_name  || MGR;
            const clkDoneLabel  = sheet.clerk_actor_name    || CLK;
            const done = (n) => <span key={n} style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>{n} ✓</span>;
            const cur = (n) => <span key={n} style={{ fontSize: 11, fontWeight: 700, color: '#d97706', padding: '1px 6px', background: '#fef3c7', borderRadius: 6 }}>{n}</span>;
            const pend = (n) => <span key={n} style={{ fontSize: 11, color: '#94a3b8' }}>{n}</span>;
            const arr = (i) => <span key={`a${i}`} style={{ color: '#cbd5e1', fontSize: 11 }}> → </span>;
            const steps = [];
            if (['pending_manager', 'pending_clerk', 'approved'].includes(sheet.status)) steps.push(done(engDoneLabel));
            else if (sheet.status === 'submitted') steps.push(cur(ENG));
            else steps.push(pend(ENG));
            if (is3) {
              steps.push(arr(1));
              if (['pending_clerk', 'approved'].includes(sheet.status)) steps.push(done(mgrDoneLabel));
              else if (['pending_manager', 'pending_engineer'].includes(sheet.status)) steps.push(cur(MGR));
              else steps.push(pend(MGR));
            }
            steps.push(arr(2));
            if (sheet.status === 'approved') steps.push(done(clkDoneLabel));
            else if (sheet.status === 'pending_clerk') steps.push(cur(CLK));
            else steps.push(pend(CLK));
            return steps;
          })()}
        </div>
      )}

      {/* AMGtime Validation Panel */}
      {/* AMGtime compact refresh bar */}
      {canSeeAmg && amgValidation?.validated && (
        <div style={{
          ...bubbleStyle, padding: '8px 14px', marginBottom: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#f8fafc',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: NAVY, fontWeight: 600 }}>AMGtime</span>
            {sheet.amgtime_dept_code && (
              <span style={{ fontSize: 10, background: '#dbeafe', color: '#1e40af', padding: '1px 6px', borderRadius: 10, fontWeight: 600 }}>
                {sheet.amgtime_dept_code}
              </span>
            )}
            {amgLoading && <span style={{ fontSize: 11, color: '#94a3b8' }}>Consultando...</span>}
            {amgError && <span style={{ fontSize: 11, color: '#ef4444' }}>{amgError}</span>}
          </div>
          <button
            onClick={() => {
              setAmgLoading(true); setAmgError(null);
              client.get(`/api/nomina/sheets/${id}/amgtime-validation`)
                .then(res => setAmgValidation(res.data))
                .catch(err => setAmgError(err.response?.data?.error || 'Error'))
                .finally(() => setAmgLoading(false));
            }}
            disabled={amgLoading}
            style={{
              fontSize: 10, padding: '2px 8px', borderRadius: 10,
              border: `1px solid ${BORDER_COLOR}`, background: '#fff',
              cursor: amgLoading ? 'not-allowed' : 'pointer', color: NAVY,
            }}
          >
            Refrescar
          </button>
        </div>
      )}

      {/* Employee groups with entries */}
      {empGroups.map(([empId, group]) => {
        const empRegular = group.entries.reduce((s, e) => s + getEntryValue(e, 'regular_hours'), 0);
        const empOvertime = group.entries.reduce((s, e) => s + getEntryValue(e, 'overtime_hours'), 0);

        return (
          <div key={empId} style={{ ...bubbleStyle, padding: 0, marginBottom: 12 }}>
            <div style={{
              padding: '10px 14px', display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', borderBottom: `1px solid ${BORDER_COLOR}`,
              background: '#f8fafc', borderRadius: '16px 16px 0 0', flexWrap: 'wrap', gap: 6,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: NAVY }}>{group.name}</span>
                <span style={{
                  fontSize: 10, fontWeight: 600, padding: '1px 8px', borderRadius: 10,
                  border: `1px solid ${group.workLocation === 'remote' ? '#7c3aed40' : '#16a34a40'}`,
                  background: group.workLocation === 'remote' ? '#f3e8ff' : '#f0fdf4',
                  color: group.workLocation === 'remote' ? '#7c3aed' : '#16a34a',
                }}>
                  {group.workLocation === 'remote' ? 'Remoto' : 'Presencial'}
                </span>
                {/* Cap bubbles for reviewer */}
                {empAlerts[empId] && (() => {
                  const a = empAlerts[empId];
                  const dailyUsed = (a.total_regular || 0) + empRegular;
                  const weeklyUsed = (a.weekly_regular || 0) + empRegular;
                  const dColor = dailyUsed > 8 ? '#ef4444' : dailyUsed >= 7 ? '#f59e0b' : '#16a34a';
                  const wColor = weeklyUsed > 40 ? '#ef4444' : weeklyUsed >= 35 ? '#f59e0b' : '#16a34a';
                  return (
                    <>
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10, background: dColor + '18', color: dColor, border: `1px solid ${dColor}40` }}>D: {dailyUsed.toFixed(1)}/8h</span>
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10, background: wColor + '18', color: wColor, border: `1px solid ${wColor}40` }}>S: {weeklyUsed.toFixed(1)}/40h</span>
                    </>
                  );
                })()}
                {/* AMGtime inline alert */}
                {canSeeAmg && (() => {
                  const amg = getAmgForEmployee(group.name);
                  if (!amg) return null;
                  if (amg.status === 'match') {
                    return (
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10, background: '#f0fdf418', color: '#16a34a', border: '1px solid #16a34a40' }}>AMG: Coincide</span>
                    );
                  }
                  if (amg.status === 'no_punch') {
                    return (
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        <AlertTriangle size={10} /> Sin ponche
                      </span>
                    );
                  }
                  const punchReg = amg.punched?.reg || 0;
                  return (
                    <span title={`AMG: ${punchReg.toFixed(1)}h REG`} style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <AlertTriangle size={10} /> AMG: {punchReg.toFixed(1)}h / Tarjeta: {empRegular.toFixed(1)}h
                    </span>
                  );
                })()}
              </div>
            </div>

            <div style={{ padding: '8px 14px' }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, fontSize: 11, fontWeight: 600, color: '#94a3b8' }}>
                <div style={{ flex: 1 }}>Tarea</div>
                <div style={{ width: 60, textAlign: 'center' }}>R</div>
                <div style={{ width: 60, textAlign: 'center' }}>E</div>
              </div>

              {group.entries.map(entry => (
                <div key={entry.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <div style={{ flex: 1, fontSize: 12, color: NAVY }}>
                    {/* GL-coded entries (gl_code_id set) render read-only — the cost-code
                        dropdown would attach a tarea and break the project's GL mode. */}
                    {editMode && entry.gl_code_id == null ? (
                      <select
                        value={getEntryValue(entry, 'cost_code_id')}
                        onChange={e => updateEditedEntry(entry.id, 'cost_code_id', e.target.value)}
                        style={{ ...fieldStyle, width: '100%', padding: '4px 6px', fontSize: 12 }}
                      >
                        <option value={entry.cost_code_id}>{entry.cost_code} ({entry.cost_code_description})</option>
                        {costCodes.filter(cc => cc.id !== entry.cost_code_id).map(cc => (
                          <option key={cc.id} value={cc.id}>{cc.code} ({cc.description})</option>
                        ))}
                      </select>
                    ) : (
                      <>{(entry.gl_code || entry.cost_code) || '—'} <span style={{ color: '#94a3b8' }}>({entry.gl_code_name || entry.cost_code_description || ''})</span></>
                    )}
                  </div>
                  <div style={{ width: 60, textAlign: 'center' }}>
                    {editMode ? (
                      <input
                        type="number"
                        value={getEntryValue(entry, 'regular_hours')}
                        onChange={e => updateEditedEntry(entry.id, 'regular_hours', e.target.value)}
                        min={0} step={0.25}
                        style={{ ...fieldStyle, width: '100%', textAlign: 'center', padding: '4px 2px', fontSize: 13 }}
                      />
                    ) : (
                      <span style={{ fontSize: 13, color: NAVY }}>{parseFloat(entry.regular_hours)}</span>
                    )}
                  </div>
                  <div style={{ width: 60, textAlign: 'center' }}>
                    {editMode ? (
                      <input
                        type="number"
                        value={getEntryValue(entry, 'overtime_hours')}
                        onChange={e => updateEditedEntry(entry.id, 'overtime_hours', e.target.value)}
                        min={0} step={0.25}
                        style={{ ...fieldStyle, width: '100%', textAlign: 'center', padding: '4px 2px', fontSize: 13 }}
                      />
                    ) : (
                      <span style={{ fontSize: 13, color: NAVY }}>{parseFloat(entry.overtime_hours)}</span>
                    )}
                  </div>
                </div>
              ))}

              {/* Employee totals */}
              <div style={{
                display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 6,
                paddingTop: 6, borderTop: `1px solid ${BORDER_COLOR}`,
                fontSize: 12, fontWeight: 600,
              }}>
                <span style={{ color: NAVY }}>{empRegular.toFixed(1)}</span>
                <span style={{ color: NAVY }}>{empOvertime.toFixed(1)}</span>
              </div>

              {/* Per-employee notes */}
              {editMode ? (
                <textarea
                  value={getEntryValue(group.entries[0], 'notes')}
                  onChange={e => updateEditedEntry(group.entries[0].id, 'notes', e.target.value)}
                  placeholder="Notas del empleado..."
                  style={{ ...fieldStyle, width: '100%', marginTop: 6, fontSize: 12, minHeight: 40, resize: 'vertical' }}
                />
              ) : group.entries[0]?.notes ? (
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 6, fontStyle: 'italic' }}>
                  {group.entries[0].notes}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}

      {/* Added employees (by reviewer) */}
      {addedEmployees.map((ae, aeIdx) => {
        const aeRegular = ae.entries.reduce((s, e) => s + (parseFloat(e.regular) || 0), 0);
        const aeOvertime = ae.entries.reduce((s, e) => s + (parseFloat(e.overtime) || 0), 0);
        return (
        <div key={`added-${aeIdx}`} style={{ ...bubbleStyle, padding: 0, marginBottom: 12, borderLeft: '3px solid #2563eb' }}>
          <div style={{
            padding: '10px 14px', display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', borderBottom: `1px solid ${BORDER_COLOR}`,
            background: '#eff6ff', borderRadius: '16px 16px 0 0', flexWrap: 'wrap', gap: 6,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: NAVY }}>{ae.empName} <span style={{ fontSize: 10, color: '#2563eb' }}>(nuevo)</span></span>
              {/* Presencial badge (default for newly added; admin-edit doesn't expose work_location switch) */}
              <span style={{
                fontSize: 10, fontWeight: 600, padding: '1px 8px', borderRadius: 10,
                border: '1px solid #16a34a40', background: '#f0fdf4', color: '#16a34a',
              }}>
                Presencial
              </span>
              {/* V26.3 Bug 3: cap bubbles for newly added employee */}
              {empAlerts[ae.empId] && (() => {
                const a = empAlerts[ae.empId];
                const dailyUsed = (a.total_regular || 0) + aeRegular;
                const weeklyUsed = (a.weekly_regular || 0) + aeRegular;
                const dColor = dailyUsed > 8 ? '#ef4444' : dailyUsed >= 7 ? '#f59e0b' : '#16a34a';
                const wColor = weeklyUsed > 40 ? '#ef4444' : weeklyUsed >= 35 ? '#f59e0b' : '#16a34a';
                return (
                  <>
                    <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10, background: dColor + '18', color: dColor, border: `1px solid ${dColor}40` }}>D: {dailyUsed.toFixed(1)}/8h</span>
                    <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10, background: wColor + '18', color: wColor, border: `1px solid ${wColor}40` }}>S: {weeklyUsed.toFixed(1)}/40h</span>
                  </>
                );
              })()}
              {/* AMGtime inline alert — reuses validation set fetched for the sheet */}
              {canSeeAmg && (() => {
                const amg = getAmgForEmployee(ae.empName);
                if (!amg) return null;
                if (amg.status === 'match') {
                  return (
                    <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10, background: '#f0fdf418', color: '#16a34a', border: '1px solid #16a34a40' }}>AMG: Coincide</span>
                  );
                }
                if (amg.status === 'no_punch') {
                  return (
                    <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <AlertTriangle size={10} /> Sin ponche
                    </span>
                  );
                }
                const punchReg = amg.punched?.reg || 0;
                return (
                  <span title={`AMG: ${punchReg.toFixed(1)}h REG`} style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                    <AlertTriangle size={10} /> AMG: {punchReg.toFixed(1)}h / Tarjeta: {aeRegular.toFixed(1)}h
                  </span>
                );
              })()}
            </div>
            <button onClick={() => setAddedEmployees(prev => prev.filter((_, i) => i !== aeIdx))} style={{
              background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2, fontSize: 12,
            }}>✕</button>
          </div>
          <div style={{ padding: '8px 14px' }}>
            {ae.entries.map((entry, eIdx) => (
              <div key={eIdx} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <SearchableSelect
                    items={costCodes.map(c => ({ id: c.id, label: `${c.code} ${c.description}` }))}
                    value={entry.costCodeId}
                    onChange={v => {
                      setAddedEmployees(prev => prev.map((a, i) => i !== aeIdx ? a : {
                        ...a, entries: a.entries.map((e, j) => j !== eIdx ? e : { ...e, costCodeId: v })
                      }));
                    }}
                    placeholder="Buscar tarea..."
                  />
                </div>
                <input type="number" value={entry.regular || ''} min={0} step={0.25} placeholder="R"
                  onChange={e => {
                    const v = parseFloat(e.target.value) || 0;
                    setAddedEmployees(prev => prev.map((a, i) => i !== aeIdx ? a : {
                      ...a, entries: a.entries.map((en, j) => j !== eIdx ? en : { ...en, regular: v })
                    }));
                  }}
                  style={{ ...fieldStyle, width: 56, textAlign: 'center', padding: '5px 2px', fontSize: 13 }}
                />
                <input type="number" value={entry.overtime || ''} min={0} step={0.25} placeholder="E"
                  onChange={e => {
                    const v = parseFloat(e.target.value) || 0;
                    setAddedEmployees(prev => prev.map((a, i) => i !== aeIdx ? a : {
                      ...a, entries: a.entries.map((en, j) => j !== eIdx ? en : { ...en, overtime: v })
                    }));
                  }}
                  style={{ ...fieldStyle, width: 56, textAlign: 'center', padding: '5px 2px', fontSize: 13 }}
                />
              </div>
            ))}
            <button onClick={() => {
              setAddedEmployees(prev => prev.map((a, i) => i !== aeIdx ? a : {
                ...a, entries: [...a.entries, { costCodeId: null, regular: 0, overtime: 0 }]
              }));
            }} style={{ fontSize: 11, color: AMBER, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0' }}>
              + Agregar tarea
            </button>
            {/* V26.3 Bug 3: per-employee notes — was missing for newly added employees */}
            <textarea
              value={ae.empNotes || ''}
              onChange={e => {
                const v = e.target.value;
                setAddedEmployees(prev => prev.map((a, i) => i !== aeIdx ? a : { ...a, empNotes: v }));
              }}
              placeholder="Notas del empleado..."
              style={{ ...fieldStyle, width: '100%', marginTop: 6, fontSize: 12, minHeight: 40, resize: 'vertical' }}
            />
          </div>
        </div>
        );
      })}

      {/* Add employee picker (in edit mode) */}
      {editMode && canReview && (
        <div style={{ marginBottom: 14, maxWidth: 300 }}>
          <SearchableSelect
            items={employees
              .filter(e => {
                const existingIds = new Set([
                  ...empGroups.map(([eid]) => parseInt(eid)),
                  ...addedEmployees.map(a => a.empId),
                ]);
                return !existingIds.has(e.id);
              })
              .map(e => ({ id: e.id, label: e.name }))}
            value={null}
            onChange={empId => {
              const emp = employees.find(e => e.id === empId);
              if (!emp) return;
              setAddedEmployees(prev => [...prev, {
                empId, empName: emp.name, empNotes: '',
                entries: [{ costCodeId: null, regular: 0, overtime: 0 }],
              }]);
            }}
            placeholder="Agregar empleado..."
          />
        </div>
      )}

      {/* Grand totals */}
      <div style={{
        ...bubbleStyle, padding: '10px 16px', marginBottom: 14,
        display: 'flex', justifyContent: 'flex-end', gap: 20,
        fontSize: 13, fontWeight: 700, color: NAVY,
      }}>
        <span>Regular: {grandRegular.toFixed(1)}</span>
        <span>Extra: {grandOvertime.toFixed(1)}</span>
        <span style={{ color: AMBER }}>Total: {(grandRegular + grandOvertime).toFixed(1)}</span>
      </div>

      {/* Unpaid absences — always rendered. Editable for current approver during review. */}
      {(
        <div style={{ ...bubbleStyle, padding: '12px 16px', marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: NAVY, letterSpacing: 0.5, marginBottom: 8 }}>
            AUSENCIAS NO REMUNERADAS
          </div>
          {(!sheet.absences || sheet.absences.length === 0) && (
            <div style={{ fontSize: 12, color: '#94a3b8' }}>Sin ausencias para esta tarjeta.</div>
          )}
          {(sheet.absences || []).map(a => {
            const isEditing = editingAbsenceId === a.id;
            const cols = canReview ? '1fr 120px 1fr auto' : '1fr 120px 1fr';
            return (
              <div key={a.id} style={{
                display: 'grid', gridTemplateColumns: cols,
                gap: 8, alignItems: 'center',
                padding: '6px 0', borderBottom: `1px solid ${BORDER_COLOR}`,
                fontSize: 13,
              }}>
                <span style={{ color: NAVY, fontWeight: 500 }}>{a.employee_name || `Empleado #${a.employee_id}`}</span>
                {isEditing ? (
                  <select
                    value={editingAbsData.reason}
                    onChange={e => setEditingAbsData(d => ({ ...d, reason: e.target.value }))}
                    style={{ ...fieldStyle, fontSize: 13 }}
                  >
                    {Object.entries(ABSENCE_REASONS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                ) : (
                  <span style={{ color: '#64748b' }}>{ABSENCE_REASONS[a.reason] || a.reason}</span>
                )}
                {isEditing ? (
                  <input
                    value={editingAbsData.notes}
                    onChange={e => setEditingAbsData(d => ({ ...d, notes: e.target.value }))}
                    placeholder="Notas"
                    style={{ ...fieldStyle, fontSize: 13 }}
                  />
                ) : (
                  <span style={{ color: a.notes ? '#64748b' : '#cbd5e1', fontStyle: a.notes ? 'normal' : 'italic' }}>
                    {a.notes || '—'}
                  </span>
                )}
                {canReview && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    {isEditing ? (
                      <>
                        <button
                          onClick={saveAbsenceEdit}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16a34a', padding: 2 }}
                          title="Guardar"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={() => setEditingAbsenceId(null)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 2 }}
                          title="Cancelar"
                        >
                          <X size={14} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEditAbsence(a)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 2 }}
                          title="Editar"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          onClick={() => removeAbsence(a.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2 }}
                          title="Eliminar"
                        >
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Sheet notes */}
      {sheet.notes && (
        <div style={{ ...bubbleStyle, padding: '12px 16px', marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: NAVY, marginBottom: 4 }}>Notas del supervisor:</div>
          <div style={{ fontSize: 13, color: '#64748b' }}>{sheet.notes}</div>
        </div>
      )}

      {/* Edit history log (Change 1) */}
      {sheet.edits && sheet.edits.length > 0 && (
        <div style={{ ...bubbleStyle, padding: '12px 16px', marginBottom: 14, background: '#f8fafc' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: NAVY, marginBottom: 6 }}>
            Historial de cambios
          </div>
          {sheet.edits.map((edit, i) => (
            <div key={i} style={{ fontSize: 11, color: '#64748b', marginBottom: 4, paddingLeft: 8, borderLeft: `2px solid ${BORDER_COLOR}` }}>
              <strong>{edit.editor_name}</strong> editó {edit.field_changed === 'cost_code_id' ? 'Tarea' : edit.field_changed === 'regular_hours' ? 'R' : 'E'} de{' '}
              <strong>{edit.old_value}</strong> → <strong>{edit.new_value}</strong> para {edit.employee_name},{' '}
              Tarea {edit.cost_code} — {fmtDateTime(edit.edited_at)}
              {edit.edit_reason && <span> — {edit.edit_reason}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Admin review actions (Change 1) */}
      {canReview && (
        <div style={{ ...bubbleStyle, padding: '16px 20px' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: NAVY, marginBottom: 10 }}>Acciones de Revisión</div>

          {/* Edit toggle */}
          {!editMode && (
            <button onClick={() => setEditMode(true)} style={{
              background: 'transparent', border: `1px solid ${BORDER_COLOR}`,
              borderRadius: 6, padding: '6px 14px', fontSize: 12, color: '#64748b',
              cursor: 'pointer', fontFamily: 'Inter, sans-serif',
              display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10,
            }}>
              <Edit3 size={13} /> Editar horas y tareas
            </button>
          )}

          {editMode && hasEdits && (
            <div style={{ marginBottom: 10 }}>
              <input
                value={editReason}
                onChange={e => setEditReason(e.target.value)}
                placeholder="Razón del cambio (opcional)..."
                style={{ ...fieldStyle, marginBottom: 6 }}
              />
            </div>
          )}

          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Notas explicativas (opcional)..."
            rows={2}
            style={{ ...fieldStyle, marginBottom: 8, resize: 'vertical' }}
          />

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={handleApprove}
              disabled={!!actionLoading || hasEdits}
              style={{
                ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
                background: '#16a34a', padding: '8px 16px', fontSize: 13,
                opacity: actionLoading || hasEdits ? 0.5 : 1,
              }}
            >
              <CheckCircle2 size={14} /> Aprobar
            </button>
            {hasEdits && (
              <button
                onClick={handleApproveWithEdits}
                disabled={!!actionLoading}
                style={{
                  ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
                  background: AMBER, padding: '8px 16px', fontSize: 13,
                  opacity: actionLoading ? 0.6 : 1,
                }}
              >
                <CheckCircle2 size={14} /> Aprobar con cambios
              </button>
            )}
            {sheet.supervisor_id !== user.id && (
              <button
                onClick={() => setShowReturnModal(true)}
                disabled={!!actionLoading}
                style={{
                  ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
                  background: '#ea580c', padding: '8px 16px', fontSize: 13,
                  opacity: actionLoading ? 0.6 : 1,
                }}
              >
                <RotateCcw size={14} /> Devolver
              </button>
            )}
            {editMode && (
              <button onClick={() => { setEditMode(false); setEditedEntries({}); }} style={{
                background: 'transparent', border: `1px solid ${BORDER_COLOR}`,
                borderRadius: 6, padding: '6px 14px', fontSize: 12, color: '#64748b',
                cursor: 'pointer', fontFamily: 'Inter, sans-serif',
              }}>
                Cancelar edición
              </button>
            )}
          </div>
        </div>
      )}

      {/* Return modal */}
      {showReturnModal && (
        <NominaSendBackModal
          sheetDate={fmtDate(sheet?.sheet_date)}
          onConfirm={handleReturn}
          onCancel={() => setShowReturnModal(false)}
        />
      )}
    </div>
  );
}
